import React, { useEffect, useState } from "react";
import { Loader2, Pencil, Save, X, Upload, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { Panel, Segmented, Field } from "../components/ui";
import RichText from "../components/RichText";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { shortDate } from "../lib/format";
import { useTranslation } from "react-i18next";
import LegalLinks from "../components/LegalLinks";
import LegalObjections from "../components/LegalObjections";

/**
 * Renders an admin-authored legal page (imprint / privacy / terms). Cookies are
 * covered inside the privacy policy rather than on a page of their own — see
 * routes_site.py.
 *
 * Two render modes:
 *   html   — server-sanitised markup. It passes through an allow-list on the way in
 *            (see routes_site.py), so what reaches dangerouslySetInnerHTML has already
 *            had scripts, event handlers and javascript: URLs stripped.
 *   iframe — an external page in a *sandboxed* frame. The sandbox omits
 *            allow-same-origin, so the embedded document cannot touch this one.
 *
 * These pages are reachable without a session, because German law requires the
 * Impressum and privacy policy to be permanently and directly accessible.
 */
export default function LegalPage({ pageKey, titleKey }) {
  const { t, i18n } = useTranslation();
  const { isAdmin } = useAuth();
  const [doc, setDoc] = useState(null);
  const [editing, setEditing] = useState(false);
  const [showUpcoming, setShowUpcoming] = useState(false);
  const [draft, setDraft] = useState({ mode: "html", html_de: "", html_en: "", iframe_url: "" });
  const [editLang, setEditLang] = useState("de");
  const [saving, setSaving] = useState(false);

  // Legal texts are stored in DE and EN together, so switching the app language must
  // refetch — the two versions are different documents to a reader, even though they
  // are one record to the API. Admins additionally get both bodies in one response so
  // the editor can hold them side by side without a second round trip.
  const lang = (i18n.language || "de").startsWith("en") ? "en" : "de";
  const load = () => api.sitePage(pageKey, lang, isAdmin)
    .then((r) => { setDoc(r.data); setDraft(r.data); }).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [pageKey, lang, isAdmin]);

  const save = async () => {
    setSaving(true);
    try {
      // Both languages go in one write, so the two can never end up saved against
      // different versions of the same policy.
      const { data } = await api.updateSitePage(pageKey, {
        mode: draft.mode,
        html_de: draft.html_de || "",
        html_en: draft.html_en || "",
        iframe_url: draft.iframe_url || "",
      });
      setDoc({ ...doc, ...data, html: data.mode === "html"
        ? (lang === "en" ? data.html_en || data.html_de : data.html_de || data.html_en)
        : "" });
      setEditing(false);
      toast.success(t("dialogs.saved"));
    } catch (e) { toast.error(e.response?.data?.detail || t("dialogs.failed")); }
    finally { setSaving(false); }
  };

  const empty = doc && !doc.html && !doc.iframe_url;
  const langLabel = (l) => t(`legal.lang.${l}`);

  // Saving is drafting; publishing is what members see, and for terms and the privacy
  // policy it asks everyone to accept again. Two separate buttons, on purpose.
  const [status, setStatus] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const loadStatus = () => {
    if (!isAdmin) return;
    api.legalStatus()
      .then((r) => setStatus((r.data || []).find((x) => x.key === pageKey) || null))
      .catch(() => {});
  };
  useEffect(() => { loadStatus(); /* eslint-disable-next-line */ }, [pageKey, isAdmin]);

  const publish = async () => {
    const needsAcceptance = status?.requires_acceptance;
    // Mirrors legal.NOTICE_REQUIRED: a *change* to the terms takes effect in four weeks
    // and is announced to every member by e-mail the moment it is published.
    const withNotice = pageKey === "terms" && status?.published_version;
    const prompt = withNotice ? "legal.publishConfirmNotice"
      : needsAcceptance ? "legal.publishConfirmAcceptance" : "legal.publishConfirm";
    let changeSummary = "";
    if (withNotice) {
      // Terms § 14.2: the notice must say what changes. Members read this in the
      // e-mail, the banner and on the terms page.
      changeSummary = (window.prompt(t("legal.changeSummaryPrompt")) || "").trim();
      if (!changeSummary) return;
    }
    if (!window.confirm(t(prompt))) return;
    setPublishing(true);
    try {
      const { data } = await api.legalPublish(pageKey, { change_summary: changeSummary });
      toast.success(t("legal.published", { version: data.version }));
      loadStatus(); load();
    } catch (e) { toast.error(e.response?.data?.detail || t("dialogs.failed")); }
    finally { setPublishing(false); }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-semibold tracking-tighter">{t(titleKey)}</h1>
          {doc?.updated_at && (
            <p className="text-xs text-slate-400 mt-1">
              {t("legal.updated", { date: shortDate(doc.updated_at) })}
              {/* Say so rather than silently serving German to someone who asked for
                  English — a reader who can't tell which language they got can't tell
                  whether it's current either. */}
              {doc.is_fallback && <span> · {t("legal.fallbackNotice")}</span>}
            </p>
          )}
        </div>
        {isAdmin && !editing && (
          <div className="flex items-center gap-2 shrink-0">
            <button className="btn-outline" onClick={() => setEditing(true)} data-testid={`edit-${pageKey}`}>
              <Pencil size={15} /> {t("legal.edit")}
            </button>
            <button className="btn-primary" onClick={publish} disabled={publishing || !status?.has_draft}
                    data-testid={`publish-${pageKey}`}>
              {publishing ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
              {t("legal.publish")}
            </button>
          </div>
        )}
      </div>

      {isAdmin && status && (
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3.5 text-xs text-slate-600 space-y-1"
             data-testid={`legal-status-${pageKey}`}>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <span>{status.published_version
              ? t("legal.publishedVersion", { version: status.published_version, date: shortDate(status.published_at) })
              : t("legal.neverPublished")}</span>
            {status.acceptance && status.published_version && (
              <span>{t("legal.acceptedBy", {
                accepted: status.acceptance.accepted, total: status.acceptance.total_members })}</span>
            )}
          </div>
          {status.has_unpublished_changes && (
            <p className="flex items-center gap-1.5 text-amber-700" data-testid={`legal-unpublished-${pageKey}`}>
              <AlertCircle size={13} /> {t("legal.unpublishedChanges")}
            </p>
          )}
          {status.requires_acceptance && (
            <p className="text-slate-400">{t("legal.acceptanceNote")}</p>
          )}
        </div>
      )}

      {isAdmin && pageKey === "terms" && <LegalObjections pageKey={pageKey} />}

      {editing ? (
        <Panel className="card p-5 space-y-4" testid={`editor-${pageKey}`}>
          <Segmented
            options={[{ value: "html", label: t("legal.modeHtml") }, { value: "iframe", label: t("legal.modeIframe") }]}
            value={draft.mode} onChange={(v) => setDraft({ ...draft, mode: v })}
            testid={`mode-${pageKey}`} fullWidthOnMobile
          />
          {draft.mode === "html" ? (
            <>
              {/* Language is a tab rather than two stacked boxes: these documents run
                  to thousands of words, and scrolling past the whole German privacy
                  policy to reach the English one is how the English one stops getting
                  updated. Both are held in the draft and written in one save. */}
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <Segmented
                  options={["de", "en"].map((l) => ({
                    value: l,
                    label: `${langLabel(l)}${(draft[`html_${l}`] || "").trim() ? "" : " ·"}`,
                  }))}
                  value={editLang} onChange={setEditLang} testid={`lang-${pageKey}`}
                />
                {!(draft[`html_${editLang}`] || "").trim() && (
                  <span className="text-[11px] text-amber-600" data-testid={`empty-${editLang}`}>
                    {t("legal.langEmpty", { lang: langLabel(editLang) })}
                  </span>
                )}
              </div>
              <Field label={t("legal.htmlLabelFor", { lang: langLabel(editLang) })}>
                <textarea
                  data-testid={`html-${pageKey}-${editLang}`}
                  className="input min-h-[320px] font-mono text-xs resize-y"
                  value={draft[`html_${editLang}`] || ""}
                  onChange={(e) => setDraft({ ...draft, [`html_${editLang}`]: e.target.value })}
                  placeholder={editLang === "de"
                    ? "<h2>Impressum</h2>&#10;<p>Angaben gemäß § 5 DDG…</p>"
                    : "<h2>Legal notice</h2>&#10;<p>Information pursuant to § 5 DDG…</p>"}
                />
                <p className="text-[11px] text-slate-400 mt-1.5">{t("legal.htmlHint")}</p>
              </Field>
            </>
          ) : (
            <Field label={t("legal.iframeLabel")}>
              <input
                data-testid={`iframe-${pageKey}`}
                className="input" value={draft.iframe_url || ""}
                onChange={(e) => setDraft({ ...draft, iframe_url: e.target.value })}
                placeholder="https://…"
              />
              <p className="text-[11px] text-slate-400 mt-1.5">{t("legal.iframeHint")}</p>
            </Field>
          )}
          <div className="flex gap-2 justify-end">
            <button className="btn-ghost" onClick={() => { setEditing(false); setDraft(doc); }}>
              <X size={15} /> {t("common.cancel")}
            </button>
            <button className="btn-primary" onClick={save} disabled={saving} data-testid={`save-${pageKey}`}>
              {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} {t("common.save")}
            </button>
          </div>
        </Panel>
      ) : (
        <Panel className="card p-5 sm:p-7" testid={`page-${pageKey}`}>
          {!doc ? (
            <Loader2 className="animate-spin text-brand-500 mx-auto my-10" size={22} />
          ) : empty ? (
            <p className="text-sm text-slate-400 text-center py-10">
              {isAdmin ? t("legal.emptyAdmin") : t("legal.empty")}
            </p>
          ) : doc.mode === "iframe" ? (
            <iframe
              title={t(titleKey)}
              src={doc.iframe_url}
              // No allow-same-origin: the embedded document is fully isolated from ours.
              sandbox="allow-popups allow-popups-to-escape-sandbox"
              referrerPolicy="no-referrer"
              className="w-full min-h-[70vh] rounded-xl border border-slate-100"
            />
          ) : (
            <>
              {/* A change in its four-week notice period (backend/legal.py): the page
                  shows the version in force and announces the new one, readable here. */}
              {doc.upcoming && (
                <div className="mb-5 rounded-xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-900"
                     data-testid="legal-upcoming">
                  <p>{t("legal.upcoming", { version: doc.upcoming.version, date: shortDate(doc.upcoming.effective_at) })}</p>
                  {doc.upcoming.change_summary && (
                    <p className="mt-1.5"><strong>{t("legal.whatChanges")}</strong> {doc.upcoming.change_summary}</p>
                  )}
                  <button className="mt-2 font-medium underline" onClick={() => setShowUpcoming((v) => !v)}>
                    {showUpcoming ? t("legal.showCurrent") : t("legal.showUpcoming")}
                  </button>
                </div>
              )}
              <RichText html={showUpcoming && doc.upcoming ? doc.upcoming.html : doc.html}
                        testid={`legal-body-${pageKey}`} />
            </>
          )}
        </Panel>
      )}

      <LegalLinks className="text-xs text-slate-400 justify-center" />
    </div>
  );
}
